import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./Outform.scss";

const OUTFORM_IMG = (file) => imageUrl("outform", file);
const EMBED_URL = "https://outform-ai.lovable.app/";

const challengeItems = [
  {
    text: "Estimation bottlenecked by a handful of senior experts",
    icon: "bulb.svg",
  },
  {
    text: "Early quotes driven by assumptions, not structured data",
    icon: "structure.svg",
  },
  {
    text: "Vendor pricing lacked systematic validation",
    icon: "cash.svg",
  },
  {
    text: "Critical cost data scattered across disconnected folders",
    icon: "document.svg",
  },
];

const featureItems = [
  {
    title: "Ballpark Estimation",
    text: "Generate early-stage cost estimates using historical project data with key drivers such as dimensions, weight, logs, and materials.",
    image: "feature-ballpark.svg",
  },
  {
    title: "Firm Estimation Support",
    text: "Benchmark vendor quotes against historical cost patterns and external factors such as freight and tariffs.",
    image: "feature-firm.svg",
  },
  {
    title: "Historical Job Retrieval",
    text: "Retrieve comparable past projects to accelerate pricing and improve accuracy.",
    image: "feature-history.svg",
  },
  {
    title: "Vendor Quote Validation",
    text: "Compare vendor pricing against historical benchmarks to identify discrepancies and missing cost components.",
    image: "feature-vendor.svg",
  },
  {
    title: "Estimator Review & Approval",
    text: "Allow estimators to review, override, and approve generated estimates to ensure accuracy and trust.",
    image: "feature-review.svg",
  },
  {
    title: "External Cost Benchmarking",
    text: "Incorporate external data such as commodity prices, freight costs, and tariffs into estimation.",
    image: "feature-benchmark.svg",
  },
  {
    title: "Workflow-Based Application",
    text: "Structure requests, vendor estimations, and manager approvals in a workflow-driven system.",
    image: "feature-workflow.svg",
  },
];

const impactItems = [
  "Accelerated estimation cycles with rapid, data-driven ballpark predictions",
  "Increased pricing accuracy through structured use of historical cost data",
  "Protected margins with automated vendor quote validation",
  "Strengthened estimator control via structured review and approval workflows",
  "Enhanced transparency with full traceability across the estimation lifecycle",
];

const stackItems = [
  { name: "Databricks", file: "logo-databricks.svg" },
  { name: "PostgreSQL", file: "logo-postgresql.svg" },
  { name: "Azure Data Lake", file: "logo-azure-data-lake.svg" },
  { name: "OpenMetadata", file: "logo-openmetadata.svg" },
  { name: "Power BI", file: "logo-powerbi.svg" },
];

const Outform = () => {
  return (
    <>
      <MegaHeader />

      <section className="outform-hero">
        <div
          className="outform-hero__background"
          aria-hidden="true"
          style={{ backgroundImage: `url(${OUTFORM_IMG("hero.svg")})` }}
        />
        <div className="outform-hero__content">
          <h1 className="outform-hero__title">
            AI-Powered Cost Estimation &amp; Quoting Platform
          </h1>
          <p className="outform-hero__description">
            Accelerated cost estimation using AI-driven ballpark predictions
            and vendor benchmarking to deliver faster, more accurate quotes.
          </p>
        </div>
      </section>

      <section className="outform-section outform-challenge">
        <div className="outform-challenge__container">
          <h2 className="outform-section__title outform-challenge__title">
            The Challenge
          </h2>
          <p className="outform-section__intro outform-challenge__intro">
            Manufacturing cost estimation often depends on spreadsheets,
            scattered historical data, and individual expertise, creating
            bottlenecks, inconsistent pricing, and long quote turnaround times
            that frustrate both sales teams and customers.
          </p>

          <div className="outform-challenge__grid">
            {challengeItems.map((item) => (
              <article key={item.text} className="outform-challenge-card">
                <div className="outform-challenge-card__icon-wrap">
                  <img
                    src={OUTFORM_IMG(item.icon)}
                    alt=""
                    aria-hidden="true"
                    className="outform-challenge-card__icon"
                  />
                </div>
                <p className="outform-challenge-card__text">{item.text}</p>
              </article>
            ))}
          </div>

          <p className="outform-challenge__closing">
            What should have been a <strong>competitive advantage, fast,
            confident quoting</strong>, became a structural weakness leading to
            delays and inaccurate pricing in the sales cycle.
          </p>
        </div>
      </section>

      <section className="outform-section outform-solution">
        <div className="outform-solution__container">
          <div className="outform-solution__header">
            <h2 className="outform-section__title">Solution</h2>
            <p className="outform-section__intro outform-solution__intro">
              The platform introduces an AI-assisted estimation system that
              supports both early-stage ballpark estimates and final cost
              validation. By leveraging historical project data and external
              benchmarks, it accelerates estimation cycles, strengthens pricing
              confidence, and equips leadership with data-backed visibility,
              while preserving estimator oversight where it matters most.
            </p>
          </div>

          <div className="outform-solution__demo-link-row">
            <a
              href={EMBED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="outform-solution__demo-link"
              aria-label="View Outform demo in a new tab"
            >
              <span>View Demo</span>
              <img
                src={OUTFORM_IMG("ViewArrow.svg")}
                alt=""
                aria-hidden="true"
                className="outform-solution__demo-link-icon"
              />
            </a>
          </div>

          <div className="outform-solution-embed-wrapper">
            <iframe
              src={EMBED_URL}
              title="Outform Cost Estimation Demo"
              className="outform-iframe"
              allowFullScreen
              allow="fullscreen"
              scrolling="yes"
            />
          </div>
        </div>
      </section>

      <section className="outform-section outform-features">
        <div className="outform-features__container">
          <h2 className="outform-section__title outform-features__title">
            Key Features
          </h2>

          <div className="outform-features__grid">
            {featureItems.map((item) => (
              <article key={item.title} className="outform-feature-card">
                <div className="outform-feature-card__media">
                  <img
                    src={OUTFORM_IMG(item.image)}
                    alt=""
                    aria-hidden="true"
                    className="outform-feature-card__image"
                  />
                </div>
                <div className="outform-feature-card__content">
                  <h3 className="outform-feature-card__title">{item.title}</h3>
                  <p className="outform-feature-card__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="outform-features__stack">
            {stackItems.map((item) => (
              <img
                key={item.name}
                src={OUTFORM_IMG(item.file)}
                alt={item.name}
                className="outform-features__stack-logo"
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className="outform-impact"
        style={{ backgroundImage: `url(${OUTFORM_IMG("footer.svg")})` }}
      >
        <div className="outform-impact__overlay" aria-hidden="true" />
        <div className="outform-impact__content">
          <h2 className="outform-section__title outform-impact__title">Impact</h2>
          <ul className="outform-impact__list">
            {impactItems.map((item) => (
              <li key={item}>
                <img
                  src={OUTFORM_IMG("checkmark.svg")}
                  alt=""
                  aria-hidden="true"
                  className="outform-impact__check"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default Outform;
